import { matchPath } from "react-router-dom";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

type BreadcrumbRule = {
  path: string;
  crumbs: (params: Record<string, string | undefined>) => BreadcrumbItem[];
};

const vehicles = (): BreadcrumbItem => ({
  labelKey: "vehicles",
});

/** Vehicles sidebar module routes — more specific paths first. */
const VEHICLES_BREADCRUMB_RULES: BreadcrumbRule[] = [
  // Vehicles list + nested
  {
    path: "/vehicles",
    crumbs: () => [vehicles(), { labelKey: "vehiclesList" }],
  },
  {
    path: "/vehicle/create",
    crumbs: () => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/vehicle/:id/invoices",
    crumbs: (p) => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      {
        labelKey: "Details",
        href: p.id ? `/vehicle/${p.id}` : undefined,
      },
      { labelKey: "invoices" },
    ],
  },
  {
    path: "/vehicle/:id/bookings",
    crumbs: (p) => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      {
        labelKey: "Details",
        href: p.id ? `/vehicle/${p.id}` : undefined,
      },
      { labelKey: "bookings" },
    ],
  },
  {
    path: "/vehicle/:id/job-cards",
    crumbs: (p) => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      {
        labelKey: "Details",
        href: p.id ? `/vehicle/${p.id}` : undefined,
      },
      { labelKey: "jobCards" },
    ],
  },
  {
    path: "/vehicle/:id/work-orders",
    crumbs: (p) => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      {
        labelKey: "Details",
        href: p.id ? `/vehicle/${p.id}` : undefined,
      },
      { labelKey: "workOrders" },
    ],
  },
  {
    path: "/vehicle/:id/inspections",
    crumbs: (p) => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      {
        labelKey: "Details",
        href: p.id ? `/vehicle/${p.id}` : undefined,
      },
      { labelKey: "Inspections" },
    ],
  },
  {
    path: "/vehicle/:id/payments",
    crumbs: (p) => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      {
        labelKey: "Details",
        href: p.id ? `/vehicle/${p.id}` : undefined,
      },
      { labelKey: "payments" },
    ],
  },
  {
    path: "/vehicle/:id/service-recommendations",
    crumbs: (p) => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      {
        labelKey: "Details",
        href: p.id ? `/vehicle/${p.id}` : undefined,
      },
      { labelKey: "serviceRecommendations" },
    ],
  },
  {
    path: "/vehicle/:id/service-history",
    crumbs: (p) => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      {
        labelKey: "Details",
        href: p.id ? `/vehicle/${p.id}` : undefined,
      },
      { labelKey: "serviceHistory" },
    ],
  },
  {
    path: "/vehicle/:id",
    crumbs: () => [
      vehicles(),
      { labelKey: "vehiclesList", href: "/vehicles" },
      { labelKey: "Details" },
    ],
  },

  // Makes
  {
    path: "/makes",
    crumbs: () => [vehicles(), { labelKey: "Makes" }],
  },
  {
    path: "/make/create",
    crumbs: () => [
      vehicles(),
      { labelKey: "Makes", href: "/makes" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/make/:id",
    crumbs: () => [
      vehicles(),
      { labelKey: "Makes", href: "/makes" },
      { labelKey: "Details" },
    ],
  },

  // Models
  {
    path: "/models",
    crumbs: () => [vehicles(), { labelKey: "Models" }],
  },
  {
    path: "/model/create",
    crumbs: () => [
      vehicles(),
      { labelKey: "Models", href: "/models" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/model/assign/service/:id",
    crumbs: () => [
      vehicles(),
      { labelKey: "Models", href: "/models" },
      { labelKey: "assignService" },
    ],
  },
  {
    path: "/model/:id",
    crumbs: () => [
      vehicles(),
      { labelKey: "Models", href: "/models" },
      { labelKey: "Details" },
    ],
  },

  // Model engines
  {
    path: "/model-engines",
    crumbs: () => [vehicles(), { labelKey: "Model Engines" }],
  },
  {
    path: "/model-engine/create",
    crumbs: () => [
      vehicles(),
      { labelKey: "Model Engines", href: "/model-engines" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/model-engine/:id",
    crumbs: () => [
      vehicles(),
      { labelKey: "Model Engines", href: "/model-engines" },
      { labelKey: "Details" },
    ],
  },

  // Vehicle types
  {
    path: "/vehicle-types",
    crumbs: () => [vehicles(), { labelKey: "vehicleTypes" }],
  },
  {
    path: "/vehicle-type/create",
    crumbs: () => [
      vehicles(),
      { labelKey: "vehicleTypes", href: "/vehicle-types" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/vehicle-type/:id",
    crumbs: () => [
      vehicles(),
      { labelKey: "vehicleTypes", href: "/vehicle-types" },
      { labelKey: "Details" },
    ],
  },

  // Vehicle statuses
  {
    path: "/vehicle-statuses",
    crumbs: () => [vehicles(), { labelKey: "VehicleStatuses" }],
  },
  {
    path: "/vehicle-status/create",
    crumbs: () => [
      vehicles(),
      { labelKey: "VehicleStatuses", href: "/vehicle-statuses" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/vehicle-status/:id",
    crumbs: () => [
      vehicles(),
      { labelKey: "VehicleStatuses", href: "/vehicle-statuses" },
      { labelKey: "Details" },
    ],
  },
];

export const getVehiclesBreadcrumbs = (
  pathname: string,
): BreadcrumbItem[] | null => {
  for (const rule of VEHICLES_BREADCRUMB_RULES) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (match) {
      return rule.crumbs(match.params as Record<string, string | undefined>);
    }
  }
  return null;
};
